import React, { useCallback, useState, useEffect } from "react";
import { RadioButton } from "primereact/radiobutton";
import ShareholderInformationForm from "./ShareholderInformationForm";
import { clearGlobalToasts, triggerGlobalToast } from "~/utils/globalToast";
import ShareholderMatchingResult from "./ShareholderMatchingResult";
import apiService from "~/services/service";
import API_ROUTES_V2 from "~/api-routes-v2";
import useDebounce from "~/hooks/useDebounce";

const ShareholderInformationWrapper = ({
  setBtnToggle,
  formDetails,
  setFormDetails,
  regData,
  setRegData,
  isEditing,
  isNewShareholder,
  shareholderType,
  setShareholderType,
  resetRegData,
  entity,
}) => {
  const [duplicates, setDuplicates] = useState({
    data: [],
    loading: false,
  });
  const debouncedGivenName = useDebounce(regData?.name, 600);
  const debouncedSurname = useDebounce(regData?.last_name, 600);
  const debouncedLegalName = useDebounce(regData?.legal_name, 600);
  const fetchDuplicateShareholders = useCallback(
    async ({ given_name, surname, legal_name, type, entity_id, tenantId }) => {
      clearGlobalToasts();
      setDuplicates({ data: [], loading: true });
      try {
        const body =
          type === "Individual"
            ? { given_name, surname, entity_id, type }
            : { legal_name, entity_id, type };

        const { data } = await apiService(
          API_ROUTES_V2.FETCH_DUPLICATE_SHAREHOLDERS,
          "POST",
          {
            body,
            replace: { tenantId: entity?.tenant_id },
            suppressToast: true,
          },
        );

        setDuplicates({ data: data || [], loading: false });

        if (data.length > 0) {
          const allLinked = data.every((director) =>
            Boolean(director.is_linked_to_entity),
          );
          const noneLinked = data.every(
            (director) => !director.is_linked_to_entity,
          );
          if (allLinked) {
            triggerGlobalToast({
              severity: "warn",
              summary:
                "Oops! This shareholder is already linked to this entity.",
              detail:
                "Review the info to avoid duplicates, or register if you believe there's an error.",
              life: 5000,
            });
            return data || [];
          }
          if (noneLinked) {
            triggerGlobalToast({
              severity: "warn",
              summary:
                "Oops! This shareholder exists but isn’t linked to this entity.",
              detail:
                "Review the info to avoid duplicates, or register if you believe there's an error.",
              life: 5000,
            });
            return data || [];
          }
          triggerGlobalToast({
            severity: "warn",
            summary:
              "Oops! A shareholder with similar details already exists and seems to be linked to this entity.",
            detail:
              "Review the info to avoid duplicates, or register if you believe there's an error.",
            life: 5000,
          });
        }

        return data || [];
      } catch (err) {
        setDuplicates({ data: [], loading: false });
        triggerGlobalToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch duplicate shareholders",
        });
      }
    },
    [entity?.tenant_id],
  );
  const statusOptions =
    shareholderType === "Individual"
      ? [
          {
            name: "Active",
            code: "Active",
            colorClass: "active-circle-fill",
          },
          {
            name: "Inactive",
            code: "Inactive",
            colorClass: "inactive-circle-fill",
          },
          {
            name: "Deceased",
            code: "Deceased",
            colorClass: "deceased-circle-fill",
          },
        ]
      : [
          {
            name: "Active",
            code: "Active",
            colorClass: "active-circle-fill",
          },
          {
            name: "Inactive",
            code: "Inactive",
            colorClass: "inactive-circle-fill",
          },
        ];

  const legalStructureOptions = [
    {
      name: "C Corp",
      code: "C Corp",
    },
    {
      name: "S Corp",
      code: "S Corp",
    },
    {
      name: "LLC",
      code: "LLC",
    },
  ];

  const resolveValue = (value, options) =>
    value && typeof value === "object"
      ? value
      : options?.find((option) => option.code === value);

  const handleRadioBtn = (e) => {
    resetRegData();
    setShareholderType(e.value);
  };

  useEffect(() => {
    if (
      shareholderType === "Individual" &&
      debouncedGivenName &&
      debouncedSurname &&
      isNewShareholder
    ) {
      fetchDuplicateShareholders({
        given_name: debouncedGivenName,
        surname: debouncedSurname,
        entity_id: entity?.id,
        type: shareholderType,
      });
    }
  }, [
    shareholderType,
    debouncedGivenName,
    debouncedSurname,
    isEditing,
    entity?.id,
    fetchDuplicateShareholders,
  ]);

  useEffect(() => {
    if (
      shareholderType === "Corporate" &&
      debouncedLegalName &&
      isNewShareholder
    ) {
      fetchDuplicateShareholders({
        legal_name: debouncedLegalName,
        entity_id: entity?.id,
        type: shareholderType,
      });
    }
  }, [
    shareholderType,
    debouncedLegalName,
    isEditing,
    entity?.id,
    fetchDuplicateShareholders,
  ]);

  useEffect(() => {
    setDuplicates({ data: [], loading: false });
  }, [shareholderType]);

  return (
    <>
      {isNewShareholder && (
        <div className="formgrid grid">
          <div className="field col-12">
            <p className="add-new-entity-heading mb-2">
              Shareholder Information
            </p>
            <p className="input-label m-0">
              Enter all the details for this appointment
            </p>
          </div>
        </div>
      )}

      <div className="formgrid grid">
        {isNewShareholder && (
          <>
            <div className="field col-12">
              <p className="appointment-para">
                Select the type of shareholder you want to register
              </p>
            </div>
            <div className="field col-6">
              <div className="appointment-card h-full">
                <div className="flex align-items-center">
                  <RadioButton
                    inputId="shareholder1"
                    name="type"
                    value="Individual"
                    onChange={handleRadioBtn}
                    checked={shareholderType === "Individual"}
                  />
                  <label htmlFor="ingredient1" className="ml-3">
                    <div className="flex flex-column justify-content-start">
                      <p className="appointment-para">Individual</p>
                      <p className="appointment-radio-para">
                        A natural person who owns shares in the company.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
            <div className="field col-6">
              <div className="appointment-card h-full">
                <div className="flex align-items-center">
                  <RadioButton
                    inputId="shareholder2"
                    name="type"
                    value="Corporate"
                    onChange={handleRadioBtn}
                    checked={shareholderType === "Corporate"}
                  />
                  <label htmlFor="ingredient2" className="ml-3">
                    <div className="flex flex-column justify-content-start">
                      <p className="appointment-para">Corporate</p>
                      <p className="appointment-radio-para">
                        A legal entity that owns shares in the company.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </>
        )}

        {shareholderType && (
          <ShareholderInformationForm
            setBtnToggle={setBtnToggle}
            formDetails={formDetails}
            setFormDetails={setFormDetails}
            regData={regData}
            setRegData={setRegData}
            isEditing={isEditing}
            shareholderType={shareholderType}
            statusOptions={statusOptions}
            legalStructureOptions={legalStructureOptions}
            resolveValue={resolveValue}
            duplicateData={duplicates.data}
            duplicateLoading={duplicates.loading}
          />
        )}
      </div>
    </>
  );
};

export default ShareholderInformationWrapper;
